import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALERT_MUTE_MAX_DAYS,
  ALERT_MUTE_REASON_MAX,
  ALERT_RESOLUTION_NOTE_MAX,
  ALERT_SNOOZE_MAX_DAYS,
  ALERT_CATEGORY_LABEL,
  addDaysYmd,
  localDateOf,
  type AlertMuteChoice,
  type AlertSnoozeChoice,
  type Role,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { shopTime } from '../settings/monitorsView';
import { useSession } from '../session';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice, StatusBadge } from '../ui/Badge';
import {
  StaleNotice,
  UnresolvedState,
  isNotFound,
  refreshFailed,
  refreshUntilShown,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { IconAlerts } from '../icons';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from '../tickets/sendOnce';
import { field } from '../create/Field';
import type { Member } from '../tickets/types';
import {
  BAND_LABEL,
  BAND_TONE,
  MUTE_CHOICES,
  SNOOZE_CHOICES,
  ageLabel,
  evidenceRows,
  historyLine,
  mayConfigureAlerts,
  mayResolveByHand,
  mayWorkAlerts,
  primaryLink,
  sourceLabel,
  stateChip,
  subjectLine,
  untilLabel,
} from './alertsView';
import type { AlertDetailData } from './types';
import { OpenTicketForm } from './OpenTicketForm';

/**
 * The alert's panel (spec §6): the shared `Sheet` (the Tickets panel, board Q1a), opened from a
 * card or a `?alert=` link. It fetches its own alert (`alert.get`), so a bare deep link from the
 * bell or the morning email opens it with nothing else on the page.
 *
 * ⚠️ The body (`AlertPanelBody`) is its own component: a Radix portal never mounts in the node
 * harness (LESSONS 2026-10-05), and the body is what the tests press.
 */
export function AlertPanel({
  alertId,
  members,
  onClose,
  onLeft,
}: {
  /** The open alert, or null for a closed panel. */
  alertId: string | null;
  members: readonly Member[];
  onClose: () => void;
  /** A write took the alert off this view (snoozed, resolved, muted): what to say. */
  onLeft: (said: string) => void;
}) {
  const trpc = useTRPC();
  const q = useQuery({
    ...trpc.alert.get.queryOptions({ alertId: alertId ?? '' }),
    enabled: alertId !== null,
  });
  const a = q.data;
  return (
    <Sheet
      open={alertId !== null}
      onClose={onClose}
      title={a ? a.label : 'Alert'}
      badge={a ? <AlertChips detail={a} /> : null}
    >
      {alertId === null ? null : (
        <AlertPanelContent alertId={alertId} members={members} onLeft={onLeft} />
      )}
    </Sheet>
  );
}

/**
 * The panel's body for one alert, read live (`alert.get`, the same read as the Sheet's title):
 * what a test renders, since the Sheet's portal never mounts in the node harness.
 */
export function AlertPanelContent({
  alertId,
  members,
  onLeft,
}: {
  alertId: string;
  members: readonly Member[];
  onLeft: (said: string) => void;
}) {
  const trpc = useTRPC();
  const q = useQuery(trpc.alert.get.queryOptions({ alertId }));
  const state = shownState(q, q.data);
  if (state === 'ready' && q.data) {
    return (
      <div className="space-y-3">
        {/* Kept, and said to be old, when a refresh failed (a write may have landed). */}
        <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
        <AlertPanelBody detail={q.data} members={members} onLeft={onLeft} />
      </div>
    );
  }
  if (state === 'error' && isNotFound(q.error)) {
    return (
      <p className="text-sm text-muted-foreground">
        That alert isn&apos;t here. The link is older than the alert, or its monitor is in
        Testing. Nothing is broken.
      </p>
    );
  }
  return (
    <UnresolvedState
      state={unresolved(state)}
      what="this alert"
      icon={IconAlerts}
      error={q.error}
      onRetry={() => void q.refetch()}
    />
  );
}

/** Band, state and category: the panel header's chips. */
export function AlertChips({ detail: a }: { detail: AlertDetailData }) {
  const state = stateChip(a, a.timeZone, Date.now());
  return (
    <span className="flex flex-wrap gap-1">
      <StatusBadge tone={BAND_TONE[a.band]}>{BAND_LABEL[a.band]}</StatusBadge>
      <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
      {a.mute ? <StatusBadge tone="neutral">Muted</StatusBadge> : null}
      <StatusBadge tone="neutral">{ALERT_CATEGORY_LABEL[a.category]}</StatusBadge>
    </span>
  );
}

type Mode = 'view' | 'assign' | 'snooze' | 'resolve' | 'mute' | 'ticket';

/**
 * Everything the panel shows of one alert and what can be done to it: the primary link to the
 * real screen, Take, Assign ▾, Snooze ▾, Resolve… (never on a ticket alert), Mute…, and Open a
 * ticket (only when the server offers a `ticketTarget`); then why it fired, what clears it, who
 * is on it, the linked ticket, and its history. Every write goes out under its own tap lock with
 * its idempotency key, and a refusal (someone took it first, it was already resolved) is said
 * in the server's words.
 */
export function AlertPanelBody({
  detail: a,
  members,
  onLeft,
}: {
  detail: AlertDetailData;
  members: readonly Member[];
  onLeft: (said: string) => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const me = useSession();
  const tz = a.timeZone;
  const now = Date.now();
  const mayWork = mayWorkAlerts(me.role);
  const [mode, setMode] = useState<Mode>('view');
  /** The button that opened a step: where the focus goes back when the step closes. */
  const opener = useRef<HTMLElement | null>(null);
  const open = (m: Mode) => {
    opener.current =
      typeof document === 'undefined'
        ? null
        : (document.activeElement as HTMLElement | null);
    setMode((cur) => (cur === m ? 'view' : m));
  };
  const close = () => {
    setMode('view');
    const el = opener.current;
    if (el?.isConnected) el.focus();
  };

  const shows = trpc.alert.pathFilter();
  const lock = useTapOnce();
  // A refused write reads the alert again (Codex r1): a refusal means the screen was stale
  // ("Dana already took this alert"), so the control that was pressed must not stay.
  const settle = {
    onError: () => refreshUntilShown(qc, shows),
    onSettled: () => lock.release(),
  };
  const stay = { ...settle, onSuccess: () => refreshUntilShown(qc, shows) };
  const take = useMutation(trpc.alert.take.mutationOptions(stay));
  const assign = useMutation(
    trpc.alert.assign.mutationOptions({
      ...settle,
      onSuccess: async () => {
        await refreshUntilShown(qc, shows);
        close();
      },
    }),
  );
  const unassign = useMutation(
    trpc.alert.unassign.mutationOptions({
      ...settle,
      onSuccess: async () => {
        await refreshUntilShown(qc, shows);
        close();
      },
    }),
  );
  const unsnooze = useMutation(trpc.alert.unsnooze.mutationOptions(stay));
  const unmute = useMutation(trpc.alert.unmute.mutationOptions(stay));
  const snooze = useMutation(
    trpc.alert.snooze.mutationOptions({
      ...settle,
      onSuccess: async (s) => {
        onLeft(
          `Snoozed${s.snoozedUntil ? ` until ${untilLabel(s.snoozedUntil, tz)}` : ''}: ${a.label}.`,
        );
        await refreshUntilShown(qc, shows);
      },
    }),
  );
  const resolve = useMutation(
    trpc.alert.resolve.mutationOptions({
      ...settle,
      onSuccess: async () => {
        onLeft(`Resolved: ${a.label}.`);
        await refreshUntilShown(qc, shows);
      },
    }),
  );
  const mute = useMutation(
    trpc.alert.mute.mutationOptions({
      ...settle,
      onSuccess: async (m) => {
        onLeft(`Muted until ${untilLabel(m.until, tz)}: ${a.label}.`);
        await refreshUntilShown(qc, shows);
      },
    }),
  );
  const writeError =
    take.error ??
    assign.error ??
    unassign.error ??
    unsnooze.error ??
    unmute.error ??
    snooze.error ??
    resolve.error ??
    mute.error;

  const alertId = a.id;
  const resolved = a.status === 'resolved';
  const snoozed = a.snoozedUntil !== null && new Date(a.snoozedUntil).getTime() > now;
  const working = mayWork && !resolved && !a.isTesting;
  const link = primaryLink(a);
  const workers = members.filter((m) => mayWorkAlerts(m.role as Role));
  const nameOf = (id: string) => members.find((m) => m.userId === id)?.name ?? null;
  const mayLiftMute =
    a.mute !== null &&
    mayWork &&
    (a.mute.by.id === me.userId || mayConfigureAlerts(me.role));
  // "Open a ticket" (ticket 04): offered exactly when the server says it may be
  // (`ticketTarget`, its one rule `openTicketRefusal`: never stock, ticket, resolved, Testing,
  // or once a ticket was opened).
  const target = mayWork ? a.ticketTarget : null;
  const mayMute = mayWork && !a.isTesting && !a.mute;

  return (
    <div className="@container space-y-6 text-sm">
      <p className="text-muted-foreground">
        {subjectLine(a, a.label)} · {sourceLabel(a.monitorKey)} ·{' '}
        {ageLabel(a.firstFiredAt, now)}
      </p>

      {a.isTesting ? (
        <Notice tone="info">
          Its monitor is in Testing: nobody works it here. Judge it in Settings.
        </Notice>
      ) : null}
      {resolved ? (
        <Notice tone="success">
          Resolved
          {a.resolvedAt ? ` ${shopTime(a.resolvedAt, tz, new Date(now))}` : ''}
          {a.resolvedKind === 'auto'
            ? ': the check no longer finds it.'
            : a.resolutionNote
              ? `: ${a.resolutionNote}`
              : '.'}
        </Notice>
      ) : null}
      {a.mute ? (
        <div className="notice badge--neutral space-y-1">
          <p>
            Muted until {untilLabel(a.mute.until, tz)} by{' '}
            {a.mute.by.name ?? 'Former staff'}: {a.mute.reason}
          </p>
          <p className="text-xs">
            Still checked, just not shown on the board or counted.
          </p>
          {mayLiftMute ? (
            <Button
              size="sm"
              variant="ghost"
              aria-disabled={unmute.isPending || undefined}
              onClick={() => sendOnce(lock, unmute.mutate, { muteId: a.mute!.id })}
            >
              Lift the mute
            </Button>
          ) : null}
        </div>
      ) : null}
      {writeError ? (
        <Notice tone="danger" alert>
          {writeError.message}
        </Notice>
      ) : null}

      <div className="flex flex-wrap gap-2" role="group" aria-label="Actions">
        <Link
          to={link.to as never}
          params={link.params as never}
          search={link.search as never}
          data-primary-link=""
          className="inline-flex items-center justify-center gap-2 rounded-[var(--radius)] bg-brand px-4 py-2 text-sm font-semibold text-ink"
        >
          {link.label}
        </Link>
        {working && a.status === 'new' ? (
          <Button
            variant="ghost"
            aria-disabled={take.isPending || undefined}
            onClick={() => sendOnce(lock, take.mutate, { alertId })}
          >
            Take it
          </Button>
        ) : null}
        {working ? (
          <Button
            variant="ghost"
            aria-expanded={mode === 'assign'}
            onClick={() => open('assign')}
          >
            Assign ▾
          </Button>
        ) : null}
        {working && snoozed ? (
          <Button
            variant="ghost"
            aria-disabled={unsnooze.isPending || undefined}
            onClick={() => sendOnce(lock, unsnooze.mutate, { alertId })}
          >
            Unsnooze
          </Button>
        ) : working ? (
          <Button
            variant="ghost"
            aria-expanded={mode === 'snooze'}
            onClick={() => open('snooze')}
          >
            Snooze ▾
          </Button>
        ) : null}
        {working && mayResolveByHand(a.subject.type) ? (
          <Button
            variant="ghost"
            aria-expanded={mode === 'resolve'}
            onClick={() => open('resolve')}
          >
            Resolve…
          </Button>
        ) : null}
        {mayMute ? (
          <Button
            variant="ghost"
            aria-expanded={mode === 'mute'}
            onClick={() => open('mute')}
          >
            Mute…
          </Button>
        ) : null}
        {target ? (
          <Button
            variant="ghost"
            aria-expanded={mode === 'ticket'}
            onClick={() => open('ticket')}
          >
            Open a ticket
          </Button>
        ) : null}
      </div>
      {working && a.subject.type === 'ticket' ? (
        <p className="text-xs text-muted-foreground">
          A ticket alert clears through its ticket: resolve the ticket and this clears
          with it.
        </p>
      ) : null}

      {working && mode === 'assign' ? (
        <AssignStep
          workers={workers}
          ownerId={a.owner?.id ?? null}
          pending={assign.isPending || unassign.isPending}
          onAssign={(ownerId) => sendOnce(lock, assign.mutate, { alertId, ownerId })}
          onUnassign={() => sendOnce(lock, unassign.mutate, { alertId })}
          onCancel={close}
        />
      ) : null}
      {working && mode === 'snooze' ? (
        <SnoozeStep
          tz={tz}
          pending={snooze.isPending}
          onSnooze={(choice, date) =>
            sendOnce(lock, snooze.mutate, {
              alertId,
              choice,
              ...(choice === 'date' ? { date } : {}),
            })
          }
          onCancel={close}
        />
      ) : null}
      {working && mode === 'resolve' ? (
        <ResolveStep
          pending={resolve.isPending}
          onResolve={(note) => sendOnce(lock, resolve.mutate, { alertId, note })}
          onCancel={close}
        />
      ) : null}
      {target && mode === 'ticket' ? (
        <OpenTicketForm alertId={alertId} target={target} onCancel={close} />
      ) : null}
      {mayMute && mode === 'mute' ? (
        <MuteStep
          tz={tz}
          pending={mute.isPending}
          onMute={(reason, choice, date) =>
            sendOnce(lock, mute.mutate, {
              alertId,
              reason,
              choice,
              ...(choice === 'date' ? { date } : {}),
            })
          }
          onCancel={close}
        />
      ) : null}

      <Section title="Why this fired">
        <dl className="space-y-2">
          {evidenceRows(a, tz, new Date(now)).map((r) => (
            <div
              key={r.label}
              className="flex flex-col gap-0.5 @sm:flex-row @sm:items-baseline @sm:justify-between @sm:gap-4"
            >
              <dt className="shrink-0 text-muted-foreground">{r.label}</dt>
              <dd className="min-w-0 break-words @sm:text-right">{r.value}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section title="Clears itself when…">
        <p>{a.clearsWhen}</p>
      </Section>

      <Section title="Owner">
        <p>
          {a.owner ? (
            (a.owner.name ?? 'Former staff')
          ) : (
            <span className="text-muted-foreground">Nobody yet</span>
          )}
        </p>
      </Section>

      {a.openedTicket ? (
        <Section title="Linked ticket">
          <Link
            to="/tickets"
            search={{ ticket: a.openedTicket.id } as never}
            data-opened-ticket=""
            className="font-medium text-[var(--sw-brand-text)] underline-offset-2 hover:underline"
          >
            Ticket #{a.openedTicket.number}
          </Link>
        </Section>
      ) : null}

      <Section title="History">
        <ol className="space-y-1">
          {a.events.map((e) => (
            <li
              key={e.id}
              className="flex flex-wrap items-baseline justify-between gap-x-3"
            >
              <span className="min-w-0 break-words">{historyLine(e, nameOf, tz)}</span>
              <span className="text-xs text-muted-foreground">
                {/* The shop's clock, in the header's style ("Oct 7, 7:00 PM"). */}
                {shopTime(e.at, tz, new Date(now))}
              </span>
            </li>
          ))}
        </ol>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="space-y-2">
      <h3
        id={id}
        className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

/** A step under the action bar: labelled, bordered, closed by Cancel or Escape-free buttons. */
function Step({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      role="group"
      aria-label={label}
      className="space-y-3 rounded-[var(--radius)] border border-border p-3"
    >
      {children}
    </div>
  );
}

/** The first control of a step takes the focus when the step opens. */
function useFocusFirst<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return ref;
}

/** "Assign ▾": each owner or office member, and "Back to the office queue". */
function AssignStep({
  workers,
  ownerId,
  pending,
  onAssign,
  onUnassign,
  onCancel,
}: {
  workers: readonly Member[];
  ownerId: string | null;
  pending: boolean;
  onAssign: (ownerId: string) => void;
  onUnassign: () => void;
  onCancel: () => void;
}) {
  const first = useFocusFirst<HTMLButtonElement>();
  return (
    <Step label="Assign to">
      <ul className="space-y-1">
        {workers.map((m, i) => (
          <li key={m.userId}>
            <button
              ref={i === 0 ? first : undefined}
              type="button"
              aria-current={m.userId === ownerId ? 'true' : undefined}
              aria-disabled={pending || undefined}
              onClick={() => {
                if (!pending && m.userId !== ownerId) onAssign(m.userId);
              }}
              className="flex w-full items-baseline justify-between gap-2 rounded-[var(--radius-sm)] px-2 py-2 text-left hover:bg-surface-2 aria-[current=true]:font-semibold"
            >
              <span className="min-w-0 truncate">{m.name ?? m.email}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {m.userId === ownerId
                  ? 'On it now'
                  : m.role === 'owner'
                    ? 'Owner'
                    : 'Office'}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        {ownerId !== null ? (
          <Button
            variant="ghost"
            size="sm"
            aria-disabled={pending || undefined}
            onClick={() => {
              if (!pending) onUnassign();
            }}
          >
            Back to the office queue
          </Button>
        ) : null}
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Step>
  );
}

/** Tomorrow's date and the last date allowed, on the shop's calendar. */
function dateBounds(tz: string, maxDays: number): { min: string; max: string } {
  const today = localDateOf(new Date(), tz);
  return { min: addDaysYmd(today, 1), max: addDaysYmd(today, maxDays) };
}

/** "Snooze ▾": the fixed choices, or 7 AM on a date (shop time) up to 30 days ahead. */
function SnoozeStep({
  tz,
  pending,
  onSnooze,
  onCancel,
}: {
  tz: string;
  pending: boolean;
  onSnooze: (choice: AlertSnoozeChoice, date?: string) => void;
  onCancel: () => void;
}) {
  const first = useFocusFirst<HTMLButtonElement>();
  const [dated, setDated] = useState(false);
  const [date, setDate] = useState('');
  const dateId = useId();
  const bounds = dateBounds(tz, ALERT_SNOOZE_MAX_DAYS);
  return (
    <Step label="Snooze until">
      <div className="flex flex-wrap gap-2">
        {SNOOZE_CHOICES.map((c, i) =>
          c.choice === 'date' ? (
            <Button
              key={c.choice}
              variant="ghost"
              size="sm"
              aria-expanded={dated}
              onClick={() => setDated((d) => !d)}
            >
              {c.label}
            </Button>
          ) : (
            <Button
              key={c.choice}
              ref={i === 0 ? first : undefined}
              variant="ghost"
              size="sm"
              aria-disabled={pending || undefined}
              onClick={() => {
                if (!pending) onSnooze(c.choice);
              }}
            >
              {c.label}
            </Button>
          ),
        )}
      </div>
      {dated ? (
        <div className="flex flex-wrap items-end gap-2">
          <label
            htmlFor={dateId}
            className="block text-xs font-medium text-muted-foreground"
          >
            Snooze until 7 AM on
            <input
              id={dateId}
              type="date"
              className={`${field} mt-1 w-auto`}
              min={bounds.min}
              max={bounds.max}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <Button
            size="sm"
            aria-disabled={pending || date === '' || undefined}
            onClick={() => {
              if (!pending && date !== '') onSnooze('date', date);
            }}
          >
            Snooze
          </Button>
        </div>
      ) : null}
      <Button variant="ghost" size="sm" onClick={onCancel}>
        Cancel
      </Button>
    </Step>
  );
}

/** "Resolve…": what was done, required. It stays resolved until the problem clears and returns. */
function ResolveStep({
  pending,
  onResolve,
  onCancel,
}: {
  pending: boolean;
  onResolve: (note: string) => void;
  onCancel: () => void;
}) {
  const first = useFocusFirst<HTMLTextAreaElement>();
  const [note, setNote] = useState('');
  const noteId = useId();
  const empty = note.trim() === '';
  return (
    <Step label="Resolve this alert">
      <label htmlFor={noteId} className="block text-xs font-medium text-muted-foreground">
        What was done
      </label>
      <textarea
        ref={first}
        id={noteId}
        maxLength={ALERT_RESOLUTION_NOTE_MAX}
        className="min-h-[70px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Called the customer; they're booking in the spring"
      />
      <p className="text-xs text-muted-foreground">
        It stays resolved until the check stops finding the problem and it comes back.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          aria-disabled={pending || empty || undefined}
          onClick={() => {
            if (!pending && !empty) onResolve(note.trim());
          }}
        >
          {empty ? 'Resolve (say what was done)' : 'Resolve'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Step>
  );
}

/** "Mute…": why (everyone sees it) and until when: 30 days, 90 days, or 7 AM on a date. */
function MuteStep({
  tz,
  pending,
  onMute,
  onCancel,
}: {
  tz: string;
  pending: boolean;
  onMute: (reason: string, choice: AlertMuteChoice, date?: string) => void;
  onCancel: () => void;
}) {
  const first = useFocusFirst<HTMLTextAreaElement>();
  const [reason, setReason] = useState('');
  const [choice, setChoice] = useState<AlertMuteChoice>('thirty_days');
  const [date, setDate] = useState('');
  const reasonId = useId();
  const dateId = useId();
  const name = useId();
  const bounds = dateBounds(tz, ALERT_MUTE_MAX_DAYS);
  const missing =
    reason.trim() === ''
      ? 'a reason'
      : choice === 'date' && date === ''
        ? 'a date'
        : null;
  return (
    <Step label="Mute this alert">
      <p className="text-xs text-muted-foreground">
        A mute hides this alert&apos;s subject until a date. It is still checked, just not
        shown, and everyone sees why.
      </p>
      <label
        htmlFor={reasonId}
        className="block text-xs font-medium text-muted-foreground"
      >
        Why
      </label>
      <textarea
        ref={first}
        id={reasonId}
        maxLength={ALERT_MUTE_REASON_MAX}
        className="min-h-[60px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Customer is away until March"
      />
      <fieldset className="space-y-1">
        <legend className="text-xs font-medium text-muted-foreground">For</legend>
        {MUTE_CHOICES.map((c) => (
          <label key={c.choice} className="flex items-center gap-2">
            <input
              type="radio"
              name={name}
              value={c.choice}
              checked={choice === c.choice}
              onChange={() => setChoice(c.choice)}
            />
            {c.label}
          </label>
        ))}
      </fieldset>
      {choice === 'date' ? (
        <label
          htmlFor={dateId}
          className="block text-xs font-medium text-muted-foreground"
        >
          Muted until 7 AM on
          <input
            id={dateId}
            type="date"
            className={`${field} mt-1 w-auto`}
            min={bounds.min}
            max={bounds.max}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          aria-disabled={pending || missing !== null || undefined}
          onClick={() => {
            if (!pending && missing === null) onMute(reason.trim(), choice, date);
          }}
        >
          {missing ? `Mute (needs ${missing})` : 'Mute'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Step>
  );
}
